import {
  faBolt,
  faBookOpen,
  faCrystalBall,
  faScroll,
  type IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";

import type { FeatureFlagState } from "../../hooks/useLaunchDarkly";
import type { NavigationLink } from "../../types";

// A surface the client owns outright: it has no row in /api/modules, so it never
// arrives with the server-side module links. Each one opens a route nested under
// Ask and is gated by the same flag as the page it opens.
export interface ClientSideModule {
  title: string;
  // Relative to the Ask route. Must stay in step with the <Route path> App.tsx
  // declares for the page.
  routeSegment: string;
  icon: IconDefinition;
  flag: keyof FeatureFlagState;
}

export const ASK_MODULE_TITLE = "Ask";

// Declaration order is nav order: the enabled ones render straight after Ask,
// in the order listed here.
export const CLIENT_SIDE_MODULES: readonly ClientSideModule[] = [
  {
    title: "Skills",
    routeSegment: "skills",
    icon: faScroll,
    flag: "showSkills",
  },
  {
    title: "Knowledge",
    routeSegment: "knowledge",
    icon: faBookOpen,
    flag: "showRexFileUpload",
  },
  {
    title: "Forecasts",
    routeSegment: "forecasts",
    icon: faCrystalBall,
    flag: "showForecasts",
  },
  {
    title: "Automations",
    routeSegment: "automations",
    icon: faBolt,
    flag: "showWorkflows",
  },
];

const CLIENT_SIDE_TITLES = new Set(CLIENT_SIDE_MODULES.map((module) => module.title));

export const isClientSideModuleLink = (link: NavigationLink): boolean =>
  CLIENT_SIDE_TITLES.has(link.title);

const toNavigationLink = (
  module: ClientSideModule,
  rootPath: string,
): NavigationLink => ({
  to: `${rootPath}/ask/rex/${module.routeSegment}`,
  title: module.title,
  fullTitle: module.title,
  icon: module.icon,
});

// Splices the enabled client-side modules in after Ask. Without Ask there is
// nothing to slot them under — every one of these routes is nested inside it —
// so the links are returned untouched.
export const withClientSideModules = (
  links: NavigationLink[],
  flags: FeatureFlagState,
  rootPath: string | undefined,
): NavigationLink[] => {
  if (!rootPath) return links;

  const askIndex = links.findIndex((link) => link.title === ASK_MODULE_TITLE);
  if (askIndex === -1) return links;

  const clientSideLinks = CLIENT_SIDE_MODULES.filter((module) => flags[module.flag]).map(
    (module) => toNavigationLink(module, rootPath),
  );

  return [
    ...links.slice(0, askIndex + 1),
    ...clientSideLinks,
    ...links.slice(askIndex + 1),
  ];
};
